import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { CurrencySelector } from '@/components/ui/CurrencySelector';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useFxRateUtils, useFxRate } from '@/hooks/useFxRates';
import { useCreateTag, useTags } from '@/hooks/useTags';
import { getTagColorClasses } from '@/lib/tagColors';
import { MerchantAutocomplete } from '@/components/transaction/MerchantAutocomplete';
import { formatDateForInput, formatCurrency } from '@/lib/utils';
import type { Transaction, CreateTransactionRequest, UpdateTransactionRequest, Budget } from '@/types';

interface TransactionFormProps {
  transaction?: Transaction;
  budget?: Budget;
  budgets?: Budget[];
  selectedTagIds?: string[];
  onTagsChange?: (tagIds: string[] | ((prev: string[]) => string[])) => void;
  /** Second arg is the final tag id list after committing any pending typed tag. */
  onSubmit: (
    data: CreateTransactionRequest | UpdateTransactionRequest,
    tagIds: string[]
  ) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

interface FormData {
  budgetId: string;
  amount: string;
  currency: string;
  date: string;
  merchant: string;
  category: string;
  note: string;
  isIncome: boolean;
}

export function TransactionForm({
  transaction,
  budget,
  budgets = [],
  selectedTagIds = [],
  onTagsChange,
  onSubmit,
  onCancel,
  isLoading,
}: TransactionFormProps) {
  const isEdit = !!transaction;
  const { getCommonCurrencies } = useFxRateUtils();
  const { data: tags = [] } = useTags();
  const createTag = useCreateTag();
  const [showDetails, setShowDetails] = React.useState(isEdit);
  const [convertedAmount, setConvertedAmount] = React.useState<number | null>(null);
  const [tagQuery, setTagQuery] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const tagInputRef = React.useRef<HTMLInputElement>(null);

  const focusTagInput = React.useCallback(() => {
    requestAnimationFrame(() => {
      tagInputRef.current?.focus();
    });
  }, []);
  
  const isHistorical = transaction 
    ? new Date(transaction.date) < new Date(new Date().toDateString())
    : false;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setFocus,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      budgetId: transaction?.budgetId ?? budget?.id ?? '',
      // show absolute value and mark income if negative
      amount: transaction
        ? Math.abs(transaction.amount).toString()
        : '',
      currency: transaction?.currency ?? budget?.currency ?? 'USD',
      date: transaction?.date 
        ? formatDateForInput(transaction.date)
        : formatDateForInput(new Date()),
      merchant: transaction?.merchant ?? '',
      category: transaction?.category ?? '',
      note: transaction?.note ?? '',
      isIncome: transaction ? transaction.amount < 0 : false,
    },
  });

  const currencies = getCommonCurrencies();
  
  const budgetOptions = budgets.map((b) => ({
    value: b.id,
    label: `${b.name} (${b.currency})`,
  }));

  const selectedBudgetId = watch('budgetId');
  const selectedBudget = budgets.find((b) => b.id === selectedBudgetId) ?? budget;
  const selectedCurrency = watch('currency');
  const selectedDate = watch('date');
  const merchantValue = watch('merchant');
  const categoryValue = watch('category');

  const availableTags = React.useMemo(
    () => tags.filter((tag) => tag.userId != null && !selectedTagIds.includes(tag.id)),
    [tags, selectedTagIds]
  );

  const matchingTags = React.useMemo(() => {
    const query = tagQuery.trim().toLowerCase();
    if (!query) return availableTags.slice(0, 8);
    return availableTags
      .filter((tag) => tag.name.toLowerCase().includes(query))
      .slice(0, 8);
  }, [availableTags, tagQuery]);

  const exactMatch = React.useMemo(() => {
    const query = tagQuery.trim().toLowerCase();
    if (!query) return null;
    return availableTags.find((tag) => tag.name.toLowerCase() === query) ?? null;
  }, [availableTags, tagQuery]);

  // Fetch exchange rate when currency differs from budget currency
  const shouldFetchRate = 
    selectedBudget && 
    selectedCurrency !== selectedBudget.currency;
  
  const { data: exchangeRateData, isLoading: isLoadingRate } = useFxRate(
    selectedCurrency,
    selectedBudget?.currency ?? 'USD',
    selectedDate
  );
  
  // useFxRate returns a numeric rate (or null); no .rate property here
  const exchangeRate = shouldFetchRate ? exchangeRateData ?? null : null;

  const busy = Boolean(isLoading || isSubmitting);

  const resolveFinalTagIds = async (): Promise<string[]> => {
    let finalTagIds = [...selectedTagIds];
    const pendingTag = tagQuery.trim();
    if (!pendingTag) return finalTagIds;

    const existing =
      availableTags.find((tag) => tag.name.toLowerCase() === pendingTag.toLowerCase()) ??
      tags.find((tag) => tag.name.toLowerCase() === pendingTag.toLowerCase());

    if (existing) {
      if (!finalTagIds.includes(existing.id)) {
        finalTagIds = [...finalTagIds, existing.id];
      }
    } else {
      const created = await createTag.mutateAsync(pendingTag);
      if (!finalTagIds.includes(created.id)) {
        finalTagIds = [...finalTagIds, created.id];
      }
    }

    setTagQuery('');
    onTagsChange?.(finalTagIds);
    return finalTagIds;
  };

  const handleFormSubmit = async (data: FormData) => {
    if (busy) return;
    setIsSubmitting(true);
    try {
      const finalTagIds = await resolveFinalTagIds();

      let amountValue = parseFloat(data.amount);
      if (data.isIncome && amountValue > 0) {
        amountValue = -amountValue;
      }

      const request = {
        budgetId: data.budgetId,
        amount: amountValue,
        currency: data.currency,
        date: data.date,
        merchant: merchantValue?.trim() || undefined,
        category: categoryValue?.trim() || undefined,
        note: data.note || undefined,
        ...(isEdit && { version: transaction.version }),
      };

      await onSubmit(
        request as CreateTransactionRequest | UpdateTransactionRequest,
        finalTagIds
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    // Auto-focus amount field on mount
    setFocus('amount');
  }, [setFocus]);

  // Calculate converted amount when amount, currency, or budget changes
  React.useEffect(() => {
    const amount = parseFloat(watch('amount'));
    
    if (
      amount && 
      selectedBudget && 
      selectedCurrency !== selectedBudget.currency &&
      exchangeRate
    ) {
      const converted = amount * exchangeRate;
      setConvertedAmount(converted);
    } else {
      setConvertedAmount(null);
    }
  }, [watch('amount'), selectedCurrency, selectedBudget, exchangeRate]);

  const addSelectedTag = React.useCallback(
    (tagId: string) => {
      if (!onTagsChange) return;
      onTagsChange((prev) => (prev.includes(tagId) ? prev : [...prev, tagId]));
      setTagQuery('');
      focusTagInput();
    },
    [focusTagInput, onTagsChange]
  );

  const removeSelectedTag = React.useCallback(
    (tagId: string) => {
      if (!onTagsChange) return;
      onTagsChange((prev) => prev.filter((id) => id !== tagId));
      focusTagInput();
    },
    [focusTagInput, onTagsChange]
  );

  const createAndAddTag = React.useCallback(async () => {
    const name = tagQuery.trim();
    if (!name || !onTagsChange || createTag.isPending) return;

    const created = await createTag.mutateAsync(name);
    onTagsChange((prev) => (prev.includes(created.id) ? prev : [...prev, created.id]));
    setTagQuery('');
    focusTagInput();
  }, [createTag, focusTagInput, onTagsChange, tagQuery]);

  const handleTagKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !tagQuery && selectedTagIds.length > 0) {
      removeSelectedTag(selectedTagIds[selectedTagIds.length - 1]);
      return;
    }

    if (e.key !== 'Enter') return;

    // Keep focus in the tag field; do not submit the transaction form.
    e.preventDefault();
    e.stopPropagation();

    if (exactMatch) {
      addSelectedTag(exactMatch.id);
      return;
    }

    if (matchingTags.length > 0 && tagQuery.trim()) {
      addSelectedTag(matchingTags[0].id);
      return;
    }

    if (tagQuery.trim()) {
      await createAndAddTag();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isEdit ? 'Edit Transaction' : 'Add Transaction'}</CardTitle>
      </CardHeader>
      <CardContent>
        {isHistorical && isEdit && (
          <div className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <strong>Warning:</strong> You are editing a historical transaction. 
            Changes may affect previously calculated FX rates.
          </div>
        )}

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {!budget && budgets.length > 0 && (
            <Select
              label="Budget"
              options={budgetOptions}
              placeholder="Select a budget"
              error={errors.budgetId?.message}
              {...register('budgetId', { required: 'Budget is required' })}
            />
          )}

          {/* Large Amount Input - Main Focus */}
          <div className="space-y-2">
            <div className="flex items-baseline gap-2 justify-center">
              <div className="flex-1 max-w-xs">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0"
                  className="text-4xl font-bold w-full text-center bg-transparent border-2 border-input rounded-lg px-4 py-3 focus:border-ring outline-none text-foreground"
                  {...register('amount', {
                    required: 'Amount is required',
                    min: { value: 0, message: 'Amount must be positive' },
                  })}
                />
              </div>
              <CurrencySelector
                value={watch('currency')}
                onChange={(newCurrency) => setValue('currency', newCurrency)}
                currencies={currencies}
              />
            </div>
            {errors.amount && (
              <p className="text-sm text-destructive text-center">{errors.amount.message}</p>
            )}
            {selectedCurrency !== selectedBudget?.currency && (
              <div className="text-sm text-muted-foreground text-center">
                {isLoadingRate ? (
                  <span>Loading rate...</span>
                ) : exchangeRate ? (
                  <>
                    <p>≈ {formatCurrency(convertedAmount ?? 0, selectedBudget?.currency ?? 'USD')}</p>
                    <p className="text-xs text-muted-foreground">
                      at {exchangeRate.toFixed(4)} {selectedBudget?.currency}
                    </p>
                  </>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400">
                    Unable to fetch exchange rate
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Date + income */}
          <div className="space-y-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">
                Date
              </label>
              <input
                type="date"
                className="w-full min-w-0 px-3 py-2 rounded-lg border border-input bg-background text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                {...register('date', { required: 'Date is required' })}
              />
              {errors.date && (
                <p className="text-xs text-destructive mt-1">{errors.date.message}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="checkbox"
                id="isIncome"
                className="h-4 w-4"
                {...register('isIncome')}
              />
              <span className="flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-600 shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9a3.75 3.75 0 117.5 0c0 2.25-3.75 3.25-3.75 4.5m0 3.5h.008" />
                </svg>
              </span>
              <label htmlFor="isIncome" className="text-sm text-green-600">
                This is an Income
              </label>
              {selectedBudget && watch('currency') !== selectedBudget.currency && (
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  Will be converted
                </span>
              )}
            </div>
          </div>

          <MerchantAutocomplete
            value={merchantValue ?? ''}
            onChange={(merchant) => setValue('merchant', merchant)}
            onSelectSuggestion={(suggestion) => {
              if (suggestion.lastCategory) {
                setValue('category', suggestion.lastCategory);
              }
            }}
          />

          <Input
            label="Category (optional)"
            placeholder="e.g., Food, Shopping, Bills"
            value={categoryValue ?? ''}
            onChange={(e) => setValue('category', e.target.value)}
          />

          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted text-sm font-medium text-foreground transition-colors"
          >
            <span>More details</span>
            <svg
              className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>

          {/* Optional details - Accordion */}
          {showDetails && (
            <div className="space-y-3 pt-2 border-t border-input">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Tags (optional)
                </label>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-input bg-background px-2 py-2">
                    {selectedTagIds
                      .map((tagId) => tags.find((tag) => tag.id === tagId))
                      .filter(Boolean)
                      .map((tag) => {
                        const palette = getTagColorClasses(tag!.id || tag!.name);
                        return (
                          <span
                            key={tag!.id}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${palette.selected}`}
                          >
                            <span>{tag!.name}</span>
                            <button
                              type="button"
                              onClick={() => removeSelectedTag(tag!.id)}
                              className="text-current/80 hover:text-current"
                              aria-label={`Remove ${tag!.name}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}

                    <input
                      ref={tagInputRef}
                      type="text"
                      value={tagQuery}
                      onChange={(e) => setTagQuery(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      disabled={!onTagsChange || busy}
                      placeholder={
                        createTag.isPending
                          ? 'Creating tag…'
                          : 'Type to search or create tag...'
                      }
                      className="min-w-[180px] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-muted-foreground"
                    />
                  </div>

                  {tagQuery.trim() && (
                    <div className="space-y-1 rounded-lg border border-input bg-background p-2">
                      {matchingTags.map((tag) => {
                        const palette = getTagColorClasses(tag.id || tag.name);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => addSelectedTag(tag.id)}
                            className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm hover:bg-muted"
                          >
                            <span>{tag.name}</span>
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] ${palette.subtle}`}>
                              existing
                            </span>
                          </button>
                        );
                      })}

                      {!exactMatch && tagQuery.trim() && (
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={createAndAddTag}
                          className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm hover:bg-muted"
                          disabled={createTag.isPending || !onTagsChange}
                        >
                          <span>Create “{tagQuery.trim()}”</span>
                          <span className="inline-flex rounded-full border border-input px-2 py-0.5 text-[11px] text-muted-foreground">
                            new
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Note (optional)
                </label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Add any additional details..."
                  {...register('note')}
                />
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={busy}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" loading={busy} disabled={busy} className="flex-1">
              {busy
                ? isEdit
                  ? 'Saving…'
                  : 'Adding…'
                : isEdit
                  ? 'Save Changes'
                  : 'Add Transaction'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
